/**
 * نقشه بازار
 * ===========
 * یک نگاه، کل بازار: هر ارز یک کاشی است؛ **بزرگی** کاشی به اندازه گردش مالی
 * آن ارز، و **رنگ** کاشی به اندازه تغییر قیمتش در ۲۴ ساعت گذشته.
 *
 * چرا اینجا از داده زنده فروشگاه استفاده می‌شود و نه درخواست تازه:
 * قیمت‌ها از قبل با وب‌سوکت به همه صفحه‌ها می‌رسند. یک درخواست دیگر فقط بار
 * اضافه روی سرور می‌گذاشت و نقشه را از بقیه صفحه عقب‌تر نگه می‌داشت.
 *
 * چیدمان از `lib/treemap.ts` می‌آید (تقسیم دودویی) تا ریاضیاتش جدا آزموده
 * شود — همان الگویی که برای اندیکاتورها هم به کار رفته.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTickers } from '../store'
import { clsx, compact, num } from '../lib/format'
import { Box, treemap } from '../lib/treemap'

type Tile = { symbol: string; base: string; fa: string; v: number; vol: number; change: number }
type Rect = Tile & Box

const COUNTS = [20, 40, 80]

export function MarketMap({ quote }: { quote: string }) {
  const nav = useNavigate()
  const rows = useTickers(quote)
  const [count, setCount] = useState(40)
  const [metric, setMetric] = useState<'volume' | 'change'>('volume')

  const tiles = useMemo<Tile[]>(() => {
    const t = rows.map((r) => {
      const vol = Math.max(r.quoteVolume || r.volume * r.last || 0, 0)
      return {
        symbol: r.symbol, base: r.base, fa: r.faName, vol, change: r.change,
        // `v` فقط وزنِ چیدمان است. گردش مالیِ واقعی جدا نگه داشته می‌شود،
        // وگرنه در حالت «شدت تغییر» عددی مثل ۳٫۱۵ سر جای مبلغ تومانی روی
        // کاشی می‌نشست و مثل مبلغ خوانده می‌شد.
        v: metric === 'volume' ? vol : Math.abs(r.change) + 0.15,
      }
    }).filter((x) => x.v > 0)
    t.sort((a, b) => b.v - a.v)
    return t.slice(0, count)
  }, [rows, count, metric])

  // خروجی بر حسب درصد است تا با هر اندازه‌ای از کارت جور دربیاید
  const rects = useMemo<Rect[]>(() => treemap(tiles, 100, 100), [tiles])

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-white/[.06] p-2 text-[11px]">
        <span className="text-slate-500">اندازه کاشی بر اساس</span>
        {([['volume', 'گردش مالی'], ['change', 'شدت تغییر']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setMetric(k)}
            className={clsx('rounded-lg px-2 py-1 transition',
              metric === k ? 'bg-brand-500/20 text-brand-200 ring-1 ring-brand-500/40'
                : 'text-slate-500 hover:bg-white/5 hover:text-slate-300')}>
            {l}
          </button>
        ))}
        <span className="h-4 w-px bg-white/10" />
        {COUNTS.map((c) => (
          <button key={c} onClick={() => setCount(c)}
            className={clsx('num rounded-lg px-2 py-1 transition',
              count === c ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300')}>
            {num(c)}
          </button>
        ))}
        <span className="ms-auto flex items-center gap-1 text-slate-500">
          <span className="h-2 w-6 rounded-sm"
            style={{ background: 'linear-gradient(90deg,#FF4D6D,rgba(255,255,255,.12),#00E39A)' }} />
          کاهش ← → افزایش
        </span>
      </div>

      <div className="relative min-h-[220px] flex-1 overflow-hidden p-1">
        {rects.length === 0 && (
          <div className="grid h-full place-items-center text-xs text-slate-500">
            در حال دریافت بازار…
          </div>
        )}
        {rects.map((r) => {
          const big = r.w > 9 && r.h > 9
          const mid = r.w > 6 && r.h > 6
          return (
            <button key={r.symbol} onClick={() => nav(`/trade/${r.symbol}`)}
              title={`${r.fa} — ${fmtPct(r.change)}`}
              className="absolute overflow-hidden rounded-md border border-ink-900/70 p-1 text-center transition hover:z-10 hover:ring-2 hover:ring-white/40"
              style={{
                left: `${r.x}%`, top: `${r.y}%`, width: `${r.w}%`, height: `${r.h}%`,
                background: tint(r.change),
              }}>
              {mid && (
                <span className="num block truncate text-[11px] font-semibold text-white" dir="ltr">
                  {r.base}
                </span>
              )}
              {big && (
                <span className="num block truncate text-[10px] text-white/85" dir="ltr">
                  {fmtPct(r.change)}
                </span>
              )}
              {big && r.h > 16 && r.vol > 0 && (
                <span className="block truncate text-[9px] text-white/60">
                  {compact(r.vol)}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const fmtPct = (v: number) => `${v > 0 ? '+' : ''}${num(v, 2)}٪`

/** رنگ کاشی: سبز برای رشد، قرمز برای افت؛ پررنگی به اندازه شدت تغییر */
function tint(change: number): string {
  const m = Math.min(Math.abs(change) / 8, 1)          // بالای ۸٪ سیر می‌شود
  const a = 0.16 + m * 0.62
  if (change > 0.02) return `rgba(0,180,120,${a})`
  if (change < -0.02) return `rgba(210,50,80,${a})`
  return 'rgba(255,255,255,.09)'
}
